// react
import { ChangeEvent, MouseEvent, useEffect, useState } from 'react';

// mui
import { Box, Grid, InputLabel, Typography } from '@mui/material';

// ui
import { IconCircleLoan } from '../../ui/IconCircleLoan/IconCircleLoan';

// i18n
import { useTranslation } from 'react-i18next';

// fields
import { Input, InputProps } from '../Input/Input';

// theme
import { theme } from '../../../theme';

// utils
import { constructAmountInputs } from '../Amounts/Amounts.utils';
import { formatCurrency } from '../../../utils/currency';

// types
import { Account, AccountBalance, Loan } from '../../../@types';

// styling
const sxStyles = {
	boxContainer: { border: `1px ${theme.palette.divider} solid`, borderRadius: '5px' },
	boxRow: {
		padding: theme.spacing(2),
		borderBottom: `1px ${theme.palette.divider} solid`,
		minWidth: '100%',
	},
};

// props
export interface AmountsTransferProps {
	balances?: AccountBalance[];
	fromAccount?: Account;
	idForm: string;
	loans?: Loan[];
	onChangeReceiving?: (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		total: number
	) => void;
	onChangeSending?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, total: number) => void;
	title: string;
	titleTotal?: string;
	toAccount?: Account;
}
export type AmountsTransferInputProps = {
	balance?: number;
	loan: Loan;
} & InputProps;

export const AmountsTransfer = ({
	balances = [],
	fromAccount,
	idForm,
	loans = [],
	onChangeReceiving,
	onChangeSending,
	title,
	titleTotal,
	toAccount,
}: AmountsTransferProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [fromAccountTotal, setFromAccountTotal] = useState<number>(0);
	const [toAccountTotal, setToAccountTotal] = useState<number>(0);

	// vars
	let amountsInputs: AmountsTransferInputProps[] = [];

	if (balances.length) {
		amountsInputs = constructAmountInputs({ balances });
	} else if (loans.length) {
		amountsInputs = constructAmountInputs({ loans });
	}

	// handlers
	const handleChangeFromAccountSource = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		const totalBalance = amountsInputs.reduce(
			(total: number, input: AmountsTransferInputProps, i: number) => {
				return total + Number(entries[`source-sending-${i}`] || 0);
			},
			0
		);

		if (onChangeSending) {
			onChangeSending(e, totalBalance);
		}

		setFromAccountTotal(totalBalance);
	};
	const handleChangeToAccountSource = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		const totalBalance = amountsInputs.reduce(
			(total: number, input: AmountsTransferInputProps, i: number) => {
				return total + Number(entries[`source-receiving-${i}`] || 0);
			},
			0
		);

		if (onChangeReceiving) {
			onChangeReceiving(e, totalBalance);
		}

		setToAccountTotal(totalBalance);
	};

	// clear inputs when loans change
	useEffect(() => {
		if (balances || loans) {
			// prioritize balances over loans
			const items = balances || loans;

			for (let i = 0; i < items.length; i++) {
				// get amount inputs
				const inputSending = document.getElementsByName(
					`source-sending-${i}`
				)[0] as HTMLInputElement;
				const inputReceiving = document.getElementsByName(
					`source-receiving-${i}`
				)[0] as HTMLInputElement;

				// reset amount
				inputSending.value = '';
				inputReceiving.value = '';
			}

			setFromAccountTotal(0);
			setToAccountTotal(0);
		}
	}, [balances, loans]);

	if (!amountsInputs.length) {
		return <></>;
	}

	return (
		<>
			<InputLabel>{title}</InputLabel>

			<Box sx={sxStyles.boxContainer}>
				<Box sx={sxStyles.boxRow}>
					<Grid container alignItems={'center'} spacing={theme.spacing(2)}>
						<Grid item xs={4}>
							<InputLabel>{t('source')}</InputLabel>
						</Grid>
						<Grid item xs={4}>
							<InputLabel required={true}>{t('sending')}</InputLabel>
						</Grid>
						<Grid item xs={4}>
							<InputLabel required={true}>{t('receiving')}</InputLabel>
						</Grid>
					</Grid>
				</Box>

				<Grid container>
					{amountsInputs.map((input: AmountsTransferInputProps, i: number) => {
						const { balance, disabled, loan, value } = input;

						let helperText: InputProps['helperText'];

						if (typeof balance === 'number') {
							helperText = t('amountAvailable', {
								amount: formatCurrency({
									currency: fromAccount?.currency,
									value: balance,
								}),
							});
						}

						return (
							<Box key={`source=${i}`} sx={sxStyles.boxRow}>
								<Grid alignItems="center" container spacing={2}>
									<Grid item xs={4}>
										<Grid alignItems="center" container spacing={1}>
											<Grid item xs="auto">
												<IconCircleLoan loan={loan} />
											</Grid>
											<Grid item xs="auto">
												<InputLabel
													disabled={disabled}
													htmlFor={`${idForm}-source-sending-${i}`}
													sx={{ margin: 0 }}>
													{loan.code}
												</InputLabel>
											</Grid>
										</Grid>
									</Grid>

									<Grid item xs={4}>
										<Input
											currency={fromAccount?.currency}
											disabled={disabled}
											helperText={helperText}
											id={`${idForm}-source-sending-${i}`}
											name={`source-sending-${i}`}
											onWheel={(e: MouseEvent) => {
												const target = e.target as HTMLElement;
												return target.blur();
											}}
											onChange={handleChangeFromAccountSource}
											type="number"
											value={value}
										/>
									</Grid>
									<Grid alignSelf="flex-start" item xs={4}>
										<Input
											currency={toAccount?.currency}
											disabled={disabled}
											id={`${idForm}-source-receiving-${i}`}
											name={`source-receiving-${i}`}
											onWheel={(e: MouseEvent) => {
												const target = e.target as HTMLElement;
												return target.blur();
											}}
											onChange={handleChangeToAccountSource}
											type="number"
											value={value}
										/>
									</Grid>
								</Grid>
							</Box>
						);
					})}
					<Box
						sx={{
							...sxStyles.boxRow,
							border: 'none',
						}}>
						<Grid container alignItems={'center'} spacing={theme.spacing(2)}>
							<Grid item xs={4}>
								<Typography fontWeight={600}>{titleTotal || t('totalAmount')}</Typography>
							</Grid>
							<Grid item xs={4}>
								<Typography fontWeight={600}>
									{formatCurrency({
										currency: fromAccount?.currency?.toString(),
										value: fromAccountTotal,
									})}
								</Typography>
							</Grid>
							<Grid item xs={4}>
								<Typography fontWeight={600} color={theme.palette.brand.grey[500]}>
									{formatCurrency({
										currency: toAccount?.currency?.toString(),
										value: toAccountTotal,
									})}
								</Typography>
							</Grid>
						</Grid>
					</Box>
				</Grid>
			</Box>
		</>
	);
};
